import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { formatDongDisplayName } from '../utils/dong';

interface Unit {
  id: number;
  floor: number;
  suffix: number;
  code: string;
  buildingId: number;
  building: {
    id: number;
    dongNumber: number;
    dongName?: string | null;
    complex: {
      id: number;
      name: string;
      code: number;
    };
  };
}

interface CardDetailResponse {
  message: string;
  data: {
    id: number;
    name: string;
    createdAt: string;
    ruleJson: string;
    ruleData: any;
    units: Unit[];
    unitsByComplex: Record<string, Record<string, Unit[]>>;
    summary: {
      totalUnits: number;
      complexCount: number;
      buildingCount: number;
    };
  };
}

const fetchCardDetail = async (cardId: number): Promise<CardDetailResponse> => {
  const response = await fetch(`/api/cards/${cardId}`);
  if (!response.ok) {
    throw new Error('카드 상세 정보를 불러오는데 실패했습니다');
  }
  return response.json();
};

// Utility function to extract pure unit code without building ID prefix
const extractPureUnitCode = (code: string): string => {
  // Remove building ID prefix (e.g., "13-1601" -> "1601", "14-1001" -> "1001")
  const match = code.match(/^\d+-(.+)$/);
  return match ? match[1]! : code;
};

const downloadCSV = (card: CardDetailResponse['data']) => {
  // Create CSV content
  const headers = ['세대코드', '층', '호수', '동', '단지명', '단지코드'];
  const rows = card.units.map(unit => [
    extractPureUnitCode(unit.code),
    unit.floor.toString(),
    unit.suffix.toString(),
    formatDongDisplayName(unit.building.dongNumber, unit.building.dongName),
    unit.building.complex.name,
    unit.building.complex.code.toString(),
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(field => `"${field}"`).join(',')),
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute(
    'download',
    `visit-card-${card.name}-${new Date().toISOString().split('T')[0]}.csv`
  );
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  toast.success('CSV 파일이 성공적으로 다운로드되었습니다!');
};

const CardDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const cardId = parseInt(id || '0');

  const { data, isLoading, error } = useQuery({
    queryKey: ['card', cardId],
    queryFn: () => fetchCardDetail(cardId),
    enabled: !!cardId,
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="page-wide">
        <div className="card p-4 sm:p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="h-64 bg-gray-200 rounded"></div>
              <div className="lg:col-span-2 h-96 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="page-wide">
        <div className="card card-body text-center">
          <h1 className="page-title mb-2">카드를 찾을 수 없습니다</h1>
          <p className="text-gray-500 text-sm mb-4">ID {id}에 해당하는 카드를 찾을 수 없습니다.</p>
          <Link to="/cards" className="text-brand-600 hover:text-brand-700 text-sm">
            ← 카드 목록으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  const card = data.data;

  return (
    <div className="page-wide">
      <div className="card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="page-title">{card.name}</h1>
            <p className="page-subtitle">방문 카드 상세 정보</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => downloadCSV(card)}
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
            >
              CSV 다운로드
            </button>
            <Link
              to="/cards"
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded inline-block"
            >
              카드 목록으로
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4">카드 정보</h2>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    카드 ID:
                  </span>
                  <p className="text-gray-900">{card.id}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    카드명:
                  </span>
                  <p className="text-gray-900">{card.name}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    생성일:
                  </span>
                  <p className="text-gray-900">{formatDate(card.createdAt)}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    총 세대 수:
                  </span>
                  <p className="text-gray-900">{card.summary.totalUnits}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    단지 수:
                  </span>
                  <p className="text-gray-900">{card.summary.complexCount}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    동 수:
                  </span>
                  <p className="text-gray-900">{card.summary.buildingCount}</p>
                </div>
                {card.ruleData && !card.ruleData.error && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      규칙:
                    </span>
                    <p className="text-gray-900">
                      {card.ruleData.unitsPerCard
                        ? `카드당 ${card.ruleData.unitsPerCard}개 세대`
                        : '사용자 정의 규칙'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Units by Complex Summary */}
            <div className="bg-blue-50 rounded-lg p-6 mt-6">
              <h3 className="text-lg font-semibold mb-3">분포 요약</h3>
              <div className="space-y-2">
                {Object.entries(card.unitsByComplex).map(
                  ([complexName, buildings]) => {
                    const totalUnitsInComplex = Object.values(buildings).reduce(
                      (sum, units) => sum + units.length,
                      0
                    );
                    return (
                      <div key={complexName} className="text-sm">
                        <span className="font-medium">{complexName}:</span>
                        <span className="text-gray-600 ml-2">
                          {Object.keys(buildings).length}개 동에{' '}
                          {totalUnitsInComplex}개 세대
                        </span>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">세대 목록</h2>
              <div className="text-sm text-gray-600">
                표시된 세대: {card.units.length}개 / 총 {card.summary.totalUnits}개
              </div>
            </div>
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        단지
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        동
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        층
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        세대코드
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {card.units
                      .sort((a, b) => {
                        const complexCompare = a.building.complex.name.localeCompare(
                          b.building.complex.name
                        );
                        if (complexCompare !== 0) return complexCompare;

                        const buildingCompare = a.building.dongNumber - b.building.dongNumber;
                        if (buildingCompare !== 0) return buildingCompare;

                        return b.floor - a.floor;
                      })
                      .map((unit, index) => (
                      <tr
                        key={unit.id}
                        className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {unit.building.complex.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDongDisplayName(unit.building.dongNumber, unit.building.dongName)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {unit.floor}층
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {extractPureUnitCode(unit.code)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {card.units.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    이 카드에 세대가 없습니다
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardDetailPage;
